import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';
import type {
  ReceivingDocumentDto,
  CreateReceivingDocumentRequest,
  BinOptionDto,
  CancelReceivingDocumentRequest,
  PostReceivingDocumentRequest,
  ReceivingDocumentResyncResultDto,
  EditReceivedQuantityRequest,
} from '../types/receiving.types';

class ReceivingService {
  private readonly baseUrl = '/receiving';

  async createReceivingDocument(
    request: CreateReceivingDocumentRequest
  ): Promise<ReceivingDocumentDto> {
    const { data } = await apiClient.post<ApiResponse<ReceivingDocumentDto>>(
      this.baseUrl,
      request
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to create receiving document');
  }

  async getPendingPutAway(viewAll = false): Promise<ReceivingDocumentDto[]> {
    const { data } = await apiClient.get<ApiResponse<ReceivingDocumentDto[]>>(
      this.baseUrl,
      { params: { viewAll } }
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch receiving documents');
  }

  async getById(id: number, viewAll = false): Promise<ReceivingDocumentDto> {
    const { data } = await apiClient.get<ApiResponse<ReceivingDocumentDto>>(
      `${this.baseUrl}/${id}`,
      { params: { viewAll } }
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch receiving document');
  }

  async getAll(viewAll = false): Promise<ReceivingDocumentDto[]> {
    const { data } = await apiClient.get<ApiResponse<ReceivingDocumentDto[]>>(
      `${this.baseUrl}/all`,
      { params: { viewAll } }
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch receiving documents');
  }

  async getByPurchaseOrderId(poId: number): Promise<ReceivingDocumentDto[]> {
    const { data } = await apiClient.get<ApiResponse<ReceivingDocumentDto[]>>(
      `${this.baseUrl}/by-purchase-order/${poId}`
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch receiving documents');
  }

  async getBinOptions(receivingDocumentId: number, netsuiteItemId: number): Promise<BinOptionDto[]> {
    const { data } = await apiClient.get<ApiResponse<BinOptionDto[]>>(
      `${this.baseUrl}/${receivingDocumentId}/bins/${netsuiteItemId}`
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch bin options');
  }

  async cancelDocument(
    id: number,
    request: CancelReceivingDocumentRequest = {}
  ): Promise<ReceivingDocumentDto> {
    const { data } = await apiClient.post<ApiResponse<ReceivingDocumentDto>>(
      `${this.baseUrl}/${id}/cancel`,
      request
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to cancel receiving document');
  }

  async resyncDocument(id: number): Promise<ReceivingDocumentResyncResultDto> {
    const { data } = await apiClient.post<ApiResponse<ReceivingDocumentResyncResultDto>>(
      `${this.baseUrl}/${id}/resync`
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to resync receiving document');
  }

  /**
   * 86d3kwtar — claim the document for async posting; returns 202. The caller optimistically flips
   * the row to Posting and polls getById() until PostingStatus settles (Posted / PostingFailed).
   */
  async postReceivingDocument(id: number, request: PostReceivingDocumentRequest): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<null>>(
      `${this.baseUrl}/${id}/post`,
      request
    );
    if (!data.success) throw new Error(data.message || 'Failed to start posting');
  }

  // 86d3mnuxe funnel — close a NotReached-failed posting (Failed → Cancelled) so the doc accepts a
  // fresh post. The BE refuses (400) when the post may have reached NetSuite (retry reconciles instead).
  async cancelPosting(id: number): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<null>>(`${this.baseUrl}/${id}/cancel-posting`);
    if (!data.success) throw new Error(data.message || 'Failed to cancel the posting');
  }

  // 86d3mnuxe funnel — retry a failed posting by re-arming the same job (frozen tranDate; the live
  // rebuild + adopt make one re-arm serve both the NotReached retry and the reached reconcile).
  async retryPosting(id: number): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<null>>(`${this.baseUrl}/${id}/retry-posting`);
    if (!data.success) throw new Error(data.message || 'Failed to retry the posting');
  }

  async updateTimeOut(id: number, timeOut: string): Promise<ReceivingDocumentDto> {
    const { data } = await apiClient.patch<ApiResponse<ReceivingDocumentDto>>(
      `${this.baseUrl}/${id}/timeout`,
      { timeOut }
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to update Time Out');
  }

  async editReceivedQuantity(
    docId: number,
    itemId: number,
    request: EditReceivedQuantityRequest
  ): Promise<ReceivingDocumentDto> {
    const { data } = await apiClient.patch<ApiResponse<ReceivingDocumentDto>>(
      `${this.baseUrl}/${docId}/items/${itemId}/quantity`,
      request
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to update received quantity');
  }
}

export const receivingService = new ReceivingService();
